import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { SettingsItem } from './SettingsItem';
import {
  membershipDataAtom,
  membershipLoadingAtom,
  membershipErrorAtom,
  fetchMembershipAtom,
  membershipTypeDisplayAtom,
  membershipExpirationStatusAtom,
  membershipSourceDisplayAtom,
} from '../../../../atoms/v3/membershipAtoms';

const DiamondIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 2L3 7l7 11 7-11-7-5zM6.5 7.5L10 4l3.5 3.5L10 15 6.5 7.5z" />
  </svg>
);

export const MembershipStatusItem: React.FC = () => {
  const { t } = useTranslation(['v3/me']);
  const [membership] = useAtom(membershipDataAtom);
  const [loading] = useAtom(membershipLoadingAtom);
  const [error] = useAtom(membershipErrorAtom);
  const [, fetchMembership] = useAtom(fetchMembershipAtom);
  const [typeDisplay] = useAtom(membershipTypeDisplayAtom);
  const [expirationStatus] = useAtom(membershipExpirationStatusAtom);
  const [sourceDisplay] = useAtom(membershipSourceDisplayAtom);

  // 组件挂载时获取会员信息
  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  const renderMembershipInfo = () => {
    if (loading) {
      return (
        <div className="text-sm text-ios-gray dark:text-ios-dark-gray">
          {t('v3/me:membership.loading')}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-sm text-red-500">
          {error}
        </div>
      );
    }

    if (!membership) {
      return (
        <div className="text-sm text-ios-gray dark:text-ios-dark-gray">
          {t('v3/me:membership.status.unknown')}
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{typeDisplay}</span>
          {membership.isActive && (
            <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
              ✨
            </span>
          )}
        </div>
        <div className="text-sm text-ios-gray dark:text-ios-dark-gray">
          {expirationStatus.message} • {t('v3/me:membership.source.label', '来源')}：{sourceDisplay}
        </div>
      </div>
    );
  };

  const renderMembershipBadge = () => {
    if (!membership?.isActive) {
      return null;
    }

    const getStatusColor = () => {
      switch (expirationStatus.status) {
        case 'lifetime':
          return 'bg-gradient-to-r from-purple-500 to-pink-500';
        case 'expiring':
          return 'bg-gradient-to-r from-orange-500 to-red-500';
        case 'active':
          return 'bg-gradient-to-r from-blue-500 to-purple-500';
        default:
          return 'bg-gradient-to-r from-gray-400 to-gray-500';
      }
    };

    return (
      <div className={`px-2 py-1 rounded-full text-white text-xs font-medium ${getStatusColor()}`}>
        {membership.type === 'lifetime'
          ? t('v3/me:membership.badge.lifetime')
          : t('v3/me:membership.badge.member')
        }
      </div>
    );
  };

  const handleRefresh = () => {
    fetchMembership();
  };

  return (
    <SettingsItem
      icon={
        <DiamondIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
      }
      iconBg="bg-purple-100 dark:bg-purple-900/20"
      title={t('v3/me:membership.title', '会员状态')}
      description={renderMembershipInfo()}
      rightElement={renderMembershipBadge()}
      onClick={handleRefresh}
      className="cursor-pointer hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray6 transition-colors"
    />
  );
};
